import { DatePipe } from '@angular/common'
import { Component, Input, OnDestroy, OnInit } from '@angular/core'
import { Subscription, take } from 'rxjs'
import { AnnualYieldsInformationModel } from 'src/app/models/yield-response.model'
import { AEMChunk } from 'src/app/services/AEM/aem-chunk.model'
import { CentralContentService } from 'src/app/services/content/central-content.service'
import { FundContentService } from 'src/app/services/content/fund-content.service'

@Component({
  selector: 'app-closing-price',
  templateUrl: './closing-price.component.html',
  styleUrls: ['./closing-price.component.scss'],
  providers: [],
})
export class ClosingPriceComponent implements OnInit, OnDestroy {
  @Input() priceModel: any = {}
  @Input() fundAnalyticsModel: any = {}

  public SecyieldFlag = false
  fundDefinition = 'fund-definitions'
  public staticTicker: any[] = [
    'BNDX',
    'VTIP',
    'VIPSX',
    'VTAPX',
    'VGCIX',
    'VTABX',
    'VAIPX',
    'VIPIX',
    'VTSPX',
  ]
  public staticTickerMap: any = {}

  public days: string
  public tokens: string[]
  public token: any
  profileLongName: string
  public noteUrl: string
  public noteText = ''
  public datePipe: DatePipe
  secYieldLabel: string
  aemContentSub: Subscription
  noteTextAem: AEMChunk
  public noteTextMarketPrice = ''
  public noteTextMarketValue = ''
  public noteTextYield = ''

  public annualYieldsInformation: AnnualYieldsInformationModel | null = null

  constructor(
    private readonly centralContentService: CentralContentService,
    private readonly fundContentService: FundContentService,
  ) {
    this.datePipe = new DatePipe('en-US')
  }

  public get isFundW502(): boolean {
    return (this.priceModel?.profile?.fundId as string) === 'W502'
  }

  // eslint-disable-next-line @typescript-eslint/no-empty-function
  ngOnInit(): void {
    this.noteUrl = window.location.href.split('#')[0].concat('/sec-yield')
    this.aemContentSub = this.centralContentService.aemContent$.subscribe(
      (content) => {
        this.parseAEMContent(content)
      },
    )

    if (this.isFundW502) {
      this.fundContentService
        .getYields(this.priceModel?.profile?.fundId)
        .pipe(take(1))
        .subscribe({
          next: (data) => {
            const yieldCode = 'APY'
            const apyYield = data?.content?.[0]?.yields?.find(
              (item) => item.yieldCode === yieldCode,
            )

            const percent = apyYield
              ? Number(apyYield.percent).toFixed(2)
              : null
            const asOfDate = apyYield ? apyYield.effectiveDate : null
            const footNotes = apyYield ? apyYield.footnotes : []

            this.annualYieldsInformation = {
              data,
              percent,
              asOfDate,
              footNotes,
            }
          },
          error: (error) => {
            console.error('Error fetching yields data for W502:', error)

            this.annualYieldsInformation = {
              data: null,
              percent: null,
              asOfDate: null,
              footNotes: [],
            }
          },
        })
    }
  }

  parseAEMContent(content) {
    this.staticTickerMap = {
      BNDX: this.centralContentService.getContentByBody(
        'fund_definitions_bndx',
        content,
      ),
      VTIP: this.centralContentService.getContentByBody(
        'fund_definitions_vtip',
        content,
      ),
      VIPSX: this.centralContentService.getContentByBody(
        'fund_definitions_vipsx',
        content,
      ),
      VTAPX: this.centralContentService.getContentByBody(
        'fund_definitions_vtapx',
        content,
      ),
      VGCIX: this.centralContentService.getContentByBody(
        'fund_definitions_vgcix',
        content,
      ),
      VTABX: this.centralContentService.getContentByBody(
        'fund_definitions_vtabx',
        content,
      ),
      VAIPX: this.centralContentService.getContentByBody(
        'fund_definitions_vaipx',
        content,
      ),
      VIPIX: this.centralContentService.getContentByBody(
        'fund_definitions_vipix',
        content,
      ),
      VTSPX: this.centralContentService.getContentByBody(
        'fund_definitions_vtspx',
        content,
      ),
    }
    this.noteTextMarketValue = this.centralContentService.getContentByBody(
      'fund_definitions_daily_market_value',
      content,
    ).contentValue
    this.noteTextYield = this.centralContentService.getContentByBody(
      'fund_definitions_yield',
      content,
    ).contentValue
  }
  // eslint-disable-next-line complexity
  public createDaysToken(yieldNote: any) {
    if (yieldNote !== undefined) {
      for (const y of yieldNote) {
        /* istanbul ignore next */
        if (y.footnoteText !== undefined) {
          this.days = y.footnoteText
          this.tokens = this.days.split(' ')
          for (const t of this.tokens) {
            this.token = t.trim()
            if (this.isValidToken()) {
              return this.token + ' day'
            }
          }
        }
      }
    }
  }

  isValidToken() {
    if (!isNaN(this.token) && this.token.length > 0) {
      return true
    } else {
      return false
    }
  }

  public setDialogInfo(longName = '', noteText = '', notecode = '') {
    this.profileLongName = longName
    this.noteText = notecode.concat(' - ').concat(noteText)
    if (this.profileLongName === 'Market price') {
      this.noteText = this.priceModel?.marketPriceContent['@closingMarketPrice']
    }
    if (this.profileLongName === 'Daily market value') {
      this.SecyieldFlag = true
      this.noteText = this.noteTextMarketValue
    }
    if (this.profileLongName === 'SEC yield') {
      this.SecyieldFlag = true
      this.noteText = this.noteTextYield
    }
    if (this.staticTickerMap[noteText]) {
      this.noteTextAem = this.staticTickerMap[noteText]
      this.noteText = this.noteTextAem.contentValue
    }
  }

  // For 529 funds here we are controlling closing price table width
  setClassCol5(is529) {
    return is529 ? 'col-md-5' : 'col-md-3'
  }
  setClassCol7(is529) {
    return is529 ? 'col-md-7' : 'col-md-3'
  }

  get premiumDiscountRatio(): number | null {
    const market =
      this.priceModel?.price?.currentPrice?.dailyPrice?.market?.price
    const nav = this.priceModel?.price?.currentPrice?.dailyPrice?.regular?.price

    if (market == null || nav == null || nav === 0) return null
    return (market - nav) / nav
  }
  ngOnDestroy(): void {
    this.aemContentSub.unsubscribe()
  }
}
